import { useGlobalStore, usePersistantStore } from "@/stores/global";
import { Alert, Box, CircularProgress, IconButton, Paper } from "@mui/material";
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import VerticalCalendar from "../shared/VerticalCalendar";
import SelectCalendarDialog from "./dialogs/SelectCalendarDialog";
import InnerCalendar from "./calendarElements/InnerCalendar";
import dayjs from "dayjs";
import { AddCircle, ArrowBackIos, ArrowForwardIos } from "@mui/icons-material";
import AddFastResourceDialog from "./dialogs/AddFastResource";
import OpenRequests from "./OpenRequests";
import CalendarSettings from "./dialogs/CalendarSettings";
import JobInfoDialog from "./dialogs/JobInfoDialog";
import useRights from "@/shared/api/useRights";
import PartDateSelector from "./dialogs/PartDateSelector";
import { useIsFetching } from "@tanstack/react-query";
import EditPositionDialog from "./dialogs/EditPositionDialog";
import VacationRequests from "./VacationRequests";
import HolidayModal from "./dialogs/HolidayModal";

export type PossibleCalendarTypes = "PROJECTS" | "CREWS" | "STAFF" | "RESOURCES" | "HOLIDAYS" | "BAULEITER";

export const FetchingIndicator = () => {
  const loadingIndicator = useIsFetching();
  return loadingIndicator ? <CircularProgress size={30} color="error" /> : null;
};

export default function MainCalendar() {
  const setPageTitle = useGlobalStore((state) => state.setPageTitle);

  const calendar = useRef();
  const epdref = useRef();
  const rights = useRights();

  const type = usePersistantStore((state) => state.type);
  const userId = usePersistantStore((state) => state.userId);
  const isBauleiter = type === "BAULEITER";
  const setType = usePersistantStore((state) => state.setType);
  const reference = usePersistantStore((state) => state.reference);
  const setReference = usePersistantStore((state) => state.setReference);
  const [selectedDate, setSelectedDate] = useState(null);
  const settings = usePersistantStore((state) => state.getSettings());
  const setSettings = usePersistantStore((state) => state.setSettings);
  const date = useGlobalStore((state) => state.date);

  const [jobInfoId, setJobInfoId] = useState(null);

  const [showDeletor, setShowDeletor] = useState(false);

  const setPositionEditorId = useCallback((id) => (!!epdref?.current ? epdref?.current?.open(id) : null), [epdref?.current]);

  useEffect(() => setPageTitle("Kalender"), []);

  const createDayButtons = useCallback(
    (date) => (
      <IconButton onClick={() => setSelectedDate(date)}>
        <AddCircle />
      </IconButton>
    ),
    [],
  );

  const pdfref = useRef();
  const pds = <PartDateSelector key="pds" ref={pdfref} />;

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          p: 1,
          background: "#eee",
        }}
      >
        {navigator.userAgent.toLowerCase().includes("firefox") ? (
          <Alert sx={{ mb: "10px" }} variant="filled" severity="warning">
            Der Kalender funktioniert im Firefox Browser leider nur eingeschränkt. Wir empfehlen deshalb Google Chrome oder Microsoft Edge.
          </Alert>
        ) : null}

        <Paper className={"cal" + type} sx={{ overflow: "hidden", display: "flex", flexDirection: "column" }}>
          <VerticalCalendar
            ref={calendar}
            withDragzones={!isBauleiter}
            initialCalEnd={dayjs(date).endOf("week")}
            initialCalStart={dayjs(date).startOf("week")}
            dateButtons={!isBauleiter && "DISPOSITION" in rights ? createDayButtons : null}
            buttons={
              <div className="calendarButtons">
                <SelectCalendarDialog
                  key={userId}
                  type={type}
                  reference={reference}
                  onChange={(type, reference) => {
                    setType(type);
                    setReference(reference);
                  }}
                />
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <CalendarSettings type={type} settings={settings} setSettings={setSettings} />
                  <FetchingIndicator />
                </Box>
                <div />
              </div>
            }
          >
            {!!type ? (
              <InnerCalendar
                key={`${type}-${userId}`}
                type={type}
                pdfref={pdfref}
                setJobInfoId={setJobInfoId}
                setPositionEditorId={setPositionEditorId}
                settings={settings}
                readOnly={isBauleiter || !("DISPOSITION" in rights)}
                reference={reference}
              />
            ) : (
              <Fragment />
            )}
          </VerticalCalendar>

          {!isBauleiter ? <Fragment>
            {pds}
            <AddFastResourceDialog date={selectedDate} type={type} reference={reference} close={() => setSelectedDate(null)} />
            <JobInfoDialog jobId={jobInfoId} close={() => setJobInfoId(null)} />
            <EditPositionDialog ref={epdref} />
          </Fragment> : null}
        </Paper>
      </Box>
      {!isBauleiter && "DISPOSITION" in rights && type !== "HOLIDAYS" && <OpenRequests type="LONG" pdfref={pdfref} calendar={calendar} />}
      {("DISPOSITION" in rights || "OBL" in rights) && type === "HOLIDAYS" && <HolidayModal />}
      {("DISPOSITION" in rights || "OBL" in rights) && type === "HOLIDAYS" && <VacationRequests calendar={calendar} />}
    </Box>
  );
}
